import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from '@/modes/waterSupplier/gql';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import useNotify from '../../hooks/useNotify';
import { ApolloError } from '@apollo/client/errors';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import { RecycledWaterInspectionInput } from './types';
import RecycledWaterInspectionEditForm from './RecycledWaterInspectionEditForm';

type RecycledWaterInspectionEditModalProps = {
  onClose: () => void;
  waterSiteId: string;
  siteAddressId: string;
  rwInspection: RecycledWaterInspectionInput;
};

const CREATE_RW_INSPECTION = gql(`
  mutation CreateRwInspection(
    $waterSiteId: ID!
    $input: RecycledWaterInspectionInput!
  ) {
    createRecycledWaterInspection(waterSiteId: $waterSiteId, input: $input) {
      jobId
      waterSiteId
      contactProfileId
      supervisorProfileId
      job {
        id
        addressId
      }
    }
  }
`);

const WaterSiteCreateModal: React.FC<RecycledWaterInspectionEditModalProps> = ({
  onClose,
  rwInspection,
  siteAddressId,
  waterSiteId,
}) => {
  const [inspectionInput, setInspectionInput] =
    useState<RecycledWaterInspectionInput>({
      ...rwInspection,
      outboundAssignment: rwInspection.outboundAssignment
        ? {
            ...rwInspection.outboundAssignment,
          }
        : undefined,
    });

  const { showError, showSuccess } = useNotify();
  const history = useHistory();
  const mode = useMode();

  const onCompleted = (data: any) => {
    showSuccess('Inspection saved');
    const inspection =
      data.createRecycledWaterInspection ?? data.updateRecycledWaterInspection;
    if (inspection && inspection.jobId) {
      history.push(`/${mode}/jobs/${inspection.jobId}`);
    }
    onClose();
  };

  const onError = (error: ApolloError) => {
    showError('Error saving inspection', error);
  };

  const [
    saveInspection,
    { data: saveData, loading: saveLoading, error: saveError },
  ] = useMutation(CREATE_RW_INSPECTION, {
    onCompleted,
    onError,
  });

  const save = useCallback(async () => {
    await saveInspection({
      variables: { waterSiteId, input: inspectionInput },
    });
  }, [inspectionInput]);

  return (
    <Modal onClose={onClose} title={`Create Inspection`}>
      <ModalBody>
        <RecycledWaterInspectionEditForm
          value={inspectionInput}
          siteAddressId={siteAddressId}
          onChange={setInspectionInput}
        />
      </ModalBody>
      <ModalFooter>
        <Button icon="save" disabled={saveLoading} onClick={save}>
          Save
        </Button>
        <Button ml={3} variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WaterSiteCreateModal;
